// services/photoExportService.js
// Canvas-based burn-in export - permanently renders metadata ribbons onto a photo
// Uses blob URL from cache (not presigned URL) to avoid CORS tainted canvas issues

import {
  formatBearing,
  formatCoordinates,
  formatElevation,
  formatAccuracy,
  toMGRS,
  formatCapturedAt,
  formatCapturedAtUTC,
} from "../utils/coordinateUtils";

/**
 * Burn metadata ribbons onto a photo image at full resolution.
 * Returns a JPEG blob with the ribbons permanently rendered at the top.
 *
 * @param {string} imageUrl - Blob URL or data URL for the image (must not be cross-origin)
 * @param {object} geoData - Geo metadata from the photo
 * @param {number|null} geoData.latitude
 * @param {number|null} geoData.longitude
 * @param {number|null} geoData.altitude
 * @param {number|null} geoData.bearing
 * @param {number|null} geoData.accuracy
 * @param {string|null} geoData.address
 * @param {string|null} geoData.capturedAt
 * @returns {Promise<Blob>} JPEG blob with burned-in metadata
 */
export const burnInMetadata = (imageUrl, geoData) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");

        // Draw the original image
        ctx.drawImage(img, 0, 0);

        // Calculate ribbon dimensions relative to image size
        const ribbonHeight = Math.max(28, Math.round(canvas.height * 0.035));
        const fontSize = Math.max(14, Math.round(ribbonHeight * 0.55));
        const padding = Math.round(ribbonHeight * 0.3);

        // Build ribbon text lines
        const line1 = geoData.address || "Address unavailable";

        const parts2 = [];
        const bearingStr = formatBearing(geoData.bearing);
        if (bearingStr) parts2.push(bearingStr);
        const coordsStr = formatCoordinates(geoData.latitude, geoData.longitude);
        if (coordsStr) parts2.push(coordsStr);
        const elevStr = formatElevation(geoData.altitude);
        if (elevStr) parts2.push(elevStr);
        const accStr = formatAccuracy(geoData.accuracy);
        if (accStr) parts2.push(accStr);
        const line2 = parts2.join("  |  ");

        const parts3 = [];
        const mgrsStr = toMGRS(geoData.latitude, geoData.longitude);
        if (mgrsStr) parts3.push(`MGRS: ${mgrsStr}`);
        const dateStr = formatCapturedAt(geoData.capturedAtLocal, geoData.capturedAt);
        if (dateStr) parts3.push(dateStr);
        const utcStr = formatCapturedAtUTC(geoData.capturedAt);
        if (utcStr) parts3.push(utcStr);
        const line3 = parts3.join("  |  ");

        const lines = [line1, line2];
        if (line3) lines.push(line3);

        // Draw ribbons
        ctx.font = `${fontSize}px "SF Mono", "Menlo", "Consolas", monospace`;
        ctx.textBaseline = "middle";

        lines.forEach((text, i) => {
          const y = i * ribbonHeight;

          // Semi-transparent black background
          ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
          ctx.fillRect(0, y, canvas.width, ribbonHeight);

          // Separator line between ribbons
          if (i > 0) {
            ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
          }

          // White text
          ctx.fillStyle = "white";
          ctx.fillText(text, padding, y + ribbonHeight / 2);
        });

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas toBlob returned null"));
            }
          },
          "image/jpeg",
          0.92,
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for burn-in export"));
    };

    img.src = imageUrl;
  });
};
